/**
 * Agent Designer Component
 * 
 * Visual workflow editor for creating agent configurations.
 * Can be used:
 * - Embedded within the Builder (MainApp) as a mode
 * - Standalone for creating/managing workflow presets
 * 
 * This is the SINGLE SOURCE OF TRUTH for the designer UI.
 */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';

import AgentNode from '../AgentNode';
import GroupNode from '../GroupNode';
import { WorkflowProvider } from '../../context/WorkflowContext';
import PropertyPanel from '../PropertyPanel';
import Toolbar from '../Toolbar';
import ToolLibrary from '../ToolLibrary';
import ToolEditorDialog from '../ToolEditorDialog';
import EdgeConfigDialog from '../EdgeConfigDialog';
import { useAgentConfigStore } from '../../stores/agentConfigStore';
import { knowledgeBaseApi } from '../../services/cloudApi';
import { getApiBase } from '../../services/api';
import { getAuthToken } from '../../utils/auth';

const nodeTypes = {
  agent: AgentNode,
  group: GroupNode,
};

/**
 * AgentDesigner - The visual workflow editor
 * 
 * @param {Object} props
 * @param {boolean} props.standalone - If true, renders with full chrome (header space, etc.)
 * @param {boolean} props.visible - Controls visibility (for embedded mode)
 * @param {Function} props.onWorkflowChange - Callback when workflow changes
 */
function AgentDesigner({ 
  standalone = false, 
  visible = true,
  onWorkflowChange 
}) {
  // Get shared agent config store
  const { 
    getWorkflow, 
    setWorkflow, 
    initialize,
    initialized,
    workflow: storeWorkflow,
  } = useAgentConfigStore();

  // Initialize store from cloud on first mount
  useEffect(() => {
    if (!initialized) {
      initialize();
    }
  }, [initialize, initialized]);

  // Get workflow after initialization
  const defaultWorkflow = getWorkflow();

  // Initialize with default from agent config store
  const initialNodes = defaultWorkflow.nodes || [];
  const initialEdges = defaultWorkflow.edges || [];

  const [nodes, setNodes, onNodesChangeBase] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedElements, setSelectedElements] = useState({ nodes: [], edges: [] });
  const [workflowType, setWorkflowType] = useState(defaultWorkflow.metadata?.type || 'swarm');
  const [workflowMetadata, setWorkflowMetadata] = useState(defaultWorkflow.metadata || { type: 'swarm' });
  // swarm_config is at ROOT level (not inside metadata)
  const [swarmConfig, setSwarmConfig] = useState(defaultWorkflow.swarm_config || {
    max_handoffs: 15,
    max_iterations: 20,
    execution_timeout: 900,
    node_timeout: 480
  });
  const [providers, setProviders] = useState([]);
  const [builtinTools, setBuiltinTools] = useState([]);
  const [customTools, setCustomTools] = useState(defaultWorkflow.customTools || []);
  const [showToolEditor, setShowToolEditor] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [showEdgeConfig, setShowEdgeConfig] = useState(false);
  const [editingEdge, setEditingEdge] = useState(null);

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Panel visibility states
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false);

  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  
  // Track if we just saved (to avoid setting isDirty immediately after)
  const justSavedRef = useRef(false);
  const workflowId = useAgentConfigStore((state) => state.workflowId);
  const prevWorkflowIdRef = useRef(null); // Start as null to force initial sync
  const hasInitialSyncRef = useRef(false); // Track if we've done initial sync

  // Warn user about unsaved changes before leaving
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Sync Designer changes back to shared agent config store
  useEffect(() => {
    if (nodes.length > 0) {
      const metadata = {
        ...workflowMetadata,
        type: workflowType,
      };
      const workflow = {
        nodes,
        edges,
        customTools,
        metadata,
        swarm_config: swarmConfig,  // At root level, not inside metadata
      };
      
      // After a save/load, the workflowId changes - don't mark as dirty for that first sync
      if (justSavedRef.current) {
        justSavedRef.current = false;
        useAgentConfigStore.getState().syncWorkflow?.(workflow) || setWorkflow(workflow);
      } else {
        setWorkflow(workflow);
        setHasUnsavedChanges(true);
      }
      
      if (onWorkflowChange) {
        onWorkflowChange(workflow);
      }
    }
  }, [nodes, edges, customTools, workflowType, workflowMetadata, swarmConfig, setWorkflow, onWorkflowChange]);

  // Update local state when store workflow changes (e.g., loading preset from cloud, save as new)
  useEffect(() => {
    // Sync when:
    // 1. Workflow ID changes (new preset loaded)
    // 2. First time we have valid data from store (initial sync after cloud load)
    const shouldSync = workflowId !== prevWorkflowIdRef.current || 
                       (initialized && !hasInitialSyncRef.current && storeWorkflow?.nodes?.length > 0);
    
    if (shouldSync && storeWorkflow?.nodes) {
      prevWorkflowIdRef.current = workflowId;
      hasInitialSyncRef.current = true;
      justSavedRef.current = true; // Prevent next sync from marking as dirty
      
      setNodes(storeWorkflow.nodes || []);
      setEdges(storeWorkflow.edges || []);
      setCustomTools(storeWorkflow.customTools || []);
      setWorkflowType(storeWorkflow.metadata?.type || 'swarm');
      setWorkflowMetadata(storeWorkflow.metadata || { type: 'swarm' });
      // swarm_config is at ROOT level
      setSwarmConfig(storeWorkflow.swarm_config || {
        max_handoffs: 15,
        max_iterations: 20,
        execution_timeout: 900,
        node_timeout: 480
      });
    }
  }, [workflowId, storeWorkflow, initialized, setNodes, setEdges]);

  const onNodesChange = useCallback((changes) => {
    onNodesChangeBase(changes);
  }, [onNodesChangeBase]);

  const onSelectionChange = useCallback(({ nodes: selectedNodes, edges: selectedEdges }) => {
    setSelectedElements({ nodes: selectedNodes, edges: selectedEdges });
    if (selectedNodes.length > 0) {
      setRightPanelOpen(true);
    }
  }, []);

  const selectedNodeId = selectedElements.nodes.length > 0 ? selectedElements.nodes[0].id : null;
  const selectedNode = selectedNodeId ? nodes.find(n => n.id === selectedNodeId) : null;

  // Handler for updating workflow-level settings (metadata, swarm_config)
  const handleWorkflowUpdate = useCallback((updates) => {
    if (updates.metadata) {
      setWorkflowMetadata(prev => ({ ...prev, ...updates.metadata }));
    }
    if (updates.swarm_config) {
      setSwarmConfig(prev => ({ ...prev, ...updates.swarm_config }));
    }
  }, []);

  // Function to fetch tools (including custom knowledge bases)
  const refreshTools = useCallback(async () => {
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${getApiBase()}/api/tools/builtin`, { headers });
      if (!res.ok) {
        console.warn(`Failed to fetch builtin tools: ${res.status}`);
        return;
      }
      const data = await res.json();
      let allTools = data.tools || [];
      
      // Try to fetch user's custom knowledge bases from AWS
      const authToken = getAuthToken();
      if (authToken) {
        try {
          const kbs = await knowledgeBaseApi.list();
          if (kbs?.knowledge_bases?.length > 0) {
            // Transform KBs into tool format - include FULL config for backend
            const kbTools = kbs.knowledge_bases.map(kb => ({
              id: `kb_${kb.tool_name}`,
              name: kb.tool_name,
              description: kb.tool_description,
              category: 'knowledge_bases',
              isCustomKb: true,
              kbId: kb.id,
              // Full Pinecone config for backend to generate complete tool spec
              pinecone_index_name: kb.pinecone_index_name,
              pinecone_host: kb.pinecone_host,
              pinecone_namespaces: kb.pinecone_namespaces,
              embedding_model: kb.embedding_model,
              metadata_schema: kb.metadata_schema,
            }));
            allTools = [...allTools, ...kbTools];
          }
        } catch (err) {
          console.log('Could not fetch custom KBs (user may not be authenticated)');
        }
      }
      
      setBuiltinTools(allTools);
    } catch (err) {
      console.error('Failed to fetch builtin tools:', err);
    }
  }, []);

  // Fetch providers and tools on mount
  useEffect(() => {
    const providerHeaders = {};
    const providerToken = getAuthToken();
    if (providerToken) providerHeaders['Authorization'] = `Bearer ${providerToken}`;
    fetch(`${getApiBase()}/api/providers`, { headers: providerHeaders })
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.json();
      })
      .then(data => setProviders(data.providers || []))
      .catch(err => console.error('Failed to fetch providers:', err));

    refreshTools();
  }, [refreshTools]);

  const defaultEdgeOptions = {
    type: 'smoothstep',
    pathOptions: { borderRadius: 12 },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 20,
      height: 20,
      color: '#64748b',
    },
    style: {
      strokeWidth: 2,
      stroke: '#64748b',
    },
  };

  const onConnect = useCallback(
    (params) => {
      const sourceNode = nodesRef.current.find(n => n.id === params.source);
      const targetNode = nodesRef.current.find(n => n.id === params.target);
      if (sourceNode?.type === 'agent' && targetNode?.type === 'agent') {
        const newEdge = {
          ...params,
          type: 'smoothstep',
          animated: false,
          pathOptions: { borderRadius: 12 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
            color: '#64748b',
          },
          style: {
            strokeWidth: 2,
            stroke: '#64748b',
          },
        };
        setEdges((eds) => addEdge(newEdge, eds));
      }
    },
    [setEdges]
  );

  const onEdgeClick = useCallback((event, edge) => {
    if (workflowType === 'graph') {
      setEditingEdge(edge);
      setShowEdgeConfig(true);
    }
  }, [workflowType]);

  const handleSaveEdgeConfig = useCallback((updatedEdge) => {
    if (updatedEdge._delete) {
      setEdges((eds) => eds.filter((e) => e.id !== updatedEdge.id));
    } else {
      setEdges((eds) =>
        eds.map((e) => (e.id === updatedEdge.id ? updatedEdge : e))
      );
    }
    setShowEdgeConfig(false);
    setEditingEdge(null);
  }, [setEdges]);

  const addNode = useCallback((type) => {
    if (type !== 'agent') return;
    const newNode = {
      id: `agent_${Date.now()}`,
      type: 'agent',
      position: {
        x: Math.random() * 300 + 100,
        y: Math.random() * 300 + 100
      },
      zIndex: 100,
      data: {
        label: 'Agent',
        isEntryPoint: nodes.length === 0,
        isActive: false,
        color: '#60D2CC',
        config: {
          name: `agent_${nodes.length + 1}`,
          description: '',
          model: {
            provider: 'bedrock',
            model_id: 'eu.anthropic.claude-sonnet-4-5-20250929-v1:0',
            temperature: 0.7,
            max_tokens: 4096,
            streaming: true
          },
          system_prompt: 'You are a helpful AI assistant.',
          tools: [],
          conversation_manager: {
            type: 'sliding_window',
            max_messages: 40
          },
          messages: [],
          state: {}
        }
      },
    };
    setNodes((nds) => [...nds, newNode]);
  }, [setNodes, nodes]);

  const updateNodeData = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return { ...node, data: { ...node.data, ...newData } };
        }
        return node;
      })
    );
  }, [setNodes]);

  const updateNodeConfig = useCallback((nodeId, configPath, value) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          const newConfig = JSON.parse(JSON.stringify(node.data.config || {}));
          const keys = configPath.split('.');
          let current = newConfig;
          for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) current[keys[i]] = {};
            current = current[keys[i]];
          }
          current[keys[keys.length - 1]] = value;
          return {
            ...node,
            data: { ...node.data, config: newConfig }
          };
        }
        return node;
      })
    );
  }, [setNodes]);

  const workflowContextValue = useMemo(() => ({
    providers,
    updateNodeConfig,
  }), [providers, updateNodeConfig]);

  const setEntryPoint = useCallback((nodeId) => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          isEntryPoint: node.id === nodeId
        }
      }))
    );
  }, [setNodes]);

  const deleteNode = useCallback((nodeId) => {
    setNodes((nds) => {
      const nodeToDelete = nds.find(n => n.id === nodeId);
      let newNodes;
      if (nodeToDelete?.type === 'group') {
        newNodes = nds
          .filter((node) => node.id !== nodeId)
          .map((node) => {
            if (node.parentNode === nodeId) {
              return {
                ...node,
                parentNode: undefined,
                extent: undefined,
                position: {
                  x: node.position.x + nodeToDelete.position.x,
                  y: node.position.y + nodeToDelete.position.y,
                }
              };
            }
            return node;
          });
      } else {
        newNodes = nds
          .filter((node) => node.id !== nodeId)
          .map((node) => {
            if (node.type === 'group' && node.data.childNodes?.includes(nodeId)) {
              return {
                ...node,
                data: {
                  ...node.data,
                  childNodes: node.data.childNodes.filter(id => id !== nodeId)
                }
              };
            }
            return node;
          });
      }

      const remainingAgents = newNodes.filter(n => n.type === 'agent');
      if (!remainingAgents.find(n => n.data?.isEntryPoint)) {
        if (remainingAgents.length > 0) {
          remainingAgents[0].data = { ...remainingAgents[0].data, isEntryPoint: true };
        }
      }

      return newNodes;
    });
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
  }, [setNodes, setEdges]);

  const createGroup = useCallback((subtype, selectedNodeIds = []) => {
    const groupId = `group_${Date.now()}`;
    const groupColors = {
      visual: '#6b7280',
      swarm: '#f59e0b',
      graph: '#3b82f6'
    };
    const HEADER_HEIGHT = 70;
    const CONTENT_PADDING = 16;
    const GROUP_PADDING = 16;
    const NODES_PADDING = 20;
    let groupX = 250, groupY = 100, groupWidth = 300, groupHeight = 200;
    if (selectedNodeIds.length > 0) {
      const selectedNodes = nodes.filter(n => selectedNodeIds.includes(n.id));
      if (selectedNodes.length > 0) {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        selectedNodes.forEach(node => {
          const x = node.position.x;
          const y = node.position.y;
          const width = node.width || 220;
          const height = node.height || 150;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x + width);
          maxY = Math.max(maxY, y + height);
        });
        const contentAreaOffsetX = GROUP_PADDING + CONTENT_PADDING;
        const contentAreaOffsetY = GROUP_PADDING + HEADER_HEIGHT + CONTENT_PADDING;
        groupX = minX - contentAreaOffsetX - NODES_PADDING;
        groupY = minY - contentAreaOffsetY - NODES_PADDING;
        const nodesWidth = maxX - minX;
        const nodesHeight = maxY - minY;
        const contentWidth = Math.max(250, nodesWidth + NODES_PADDING * 2);
        const contentHeight = Math.max(100, nodesHeight + NODES_PADDING * 2);
        groupWidth = Math.max(300, contentWidth + GROUP_PADDING * 2 + CONTENT_PADDING * 2);
        groupHeight = Math.max(200, contentHeight + GROUP_PADDING + HEADER_HEIGHT + CONTENT_PADDING * 2);
      }
    }
    const newGroup = {
      id: groupId,
      type: 'group',
      position: { x: groupX, y: groupY },
      zIndex: 0,
      data: {
        label: `${subtype.charAt(0).toUpperCase() + subtype.slice(1)} Group`,
        subtype: subtype,
        color: groupColors[subtype],
        expanded: true,
        childNodes: selectedNodeIds,
        description: '',
        config: subtype === 'swarm' ? {
          entry_point: selectedNodeIds[0] || null,
          max_handoffs: 20
        } : subtype === 'graph' ? {
          entry_points: [selectedNodeIds[0]] || [],
          max_node_executions: 50,
          execution_timeout: 900,
          node_timeout: null,
          reset_on_revisit: false
        } : {}
      },
      style: {
        width: groupWidth,
        height: groupHeight,
      }
    };
    setNodes((nds) => {
      const updatedNodes = nds.map((node) => {
        if (selectedNodeIds.includes(node.id)) {
          return {
            ...node,
            parentNode: groupId,
            extent: 'parent',
            selected: false,
            zIndex: 1000,
            position: {
              x: node.position.x - groupX,
              y: node.position.y - groupY,
            }
          };
        }
        return node;
      });
      const firstChildIndex = updatedNodes.findIndex(n => selectedNodeIds.includes(n.id));
      if (firstChildIndex !== -1) {
        const newNodes = [...updatedNodes];
        newNodes.splice(firstChildIndex, 0, { ...newGroup, selected: true });
        return newNodes;
      } else {
        return [...updatedNodes, { ...newGroup, selected: true }];
      }
    });
  }, [setNodes, nodes]);

  const groupSelectedNodes = useCallback((subtype) => {
    const selectedNodeIds = selectedElements.nodes.map(n => n.id);
    if (selectedNodeIds.length < 2) return;
    if (subtype !== 'visual') {
      const allAgents = selectedNodeIds.every(id => {
        const node = nodes.find(n => n.id === id);
        return node?.type === 'agent';
      });
      if (!allAgents) return;
    }
    createGroup(subtype, selectedNodeIds);
  }, [selectedElements, nodes, createGroup]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      const activeTag = document.activeElement?.tagName;
      if (['INPUT', 'SELECT', 'TEXTAREA'].includes(activeTag) || document.activeElement?.contentEditable === 'true') {
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedElements.nodes.length > 0) {
          selectedElements.nodes.forEach(node => deleteNode(node.id));
        }
        if (selectedElements.edges.length > 0) {
          setEdges((eds) =>
            eds.filter((edge) => !selectedElements.edges.find(e => e.id === edge.id))
          );
        }
      }
      if (event.key === '1' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setLeftPanelOpen(prev => !prev);
      }
      if (event.key === '2' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setRightPanelOpen(prev => !prev);
      }
      if (event.key === '3' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setBottomPanelOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElements, deleteNode, setEdges]);

  // Custom tool handlers
  const handleAddCustomTool = useCallback(() => {
    setEditingTool(null);
    setShowToolEditor(true);
  }, []);

  const handleEditCustomTool = useCallback((tool) => {
    setEditingTool(tool);
    setShowToolEditor(true);
  }, []);

  const handleSaveCustomTool = useCallback((toolData) => {
    if (editingTool) {
      setCustomTools((tools) =>
        tools.map((t) => (t.id === toolData.id ? toolData : t))
      );
    } else {
      setCustomTools((tools) => [...tools, toolData]);
    }
    setShowToolEditor(false);
    setEditingTool(null);
  }, [editingTool]);

  const handleDeleteCustomTool = useCallback((toolId) => {
    setCustomTools((tools) => tools.filter((t) => t.id !== toolId));
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          config: {
            ...node.data.config,
            tools: (node.data.config?.tools || []).filter((t) => t !== toolId)
          }
        }
      }))
    );
  }, [setNodes]);

  const handleToggleToolForAgent = useCallback((agentId, toolId) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === agentId) {
          const currentTools = node.data.config?.tools || [];
          const hasTool = currentTools.includes(toolId);
          return {
            ...node,
            data: {
              ...node.data,
              config: {
                ...node.data.config,
                tools: hasTool
                  ? currentTools.filter((t) => t !== toolId)
                  : [...currentTools, toolId]
              }
            }
          };
        }
        return node;
      })
    );
  }, [setNodes]);

  // Save/Load workflow
  const saveWorkflow = useCallback(() => {
    const workflow = {
      nodes: nodes.map(node => ({
        id: node.id,
        type: node.type,
        data: node.data,
        position: node.position,
        style: node.style,
        width: node.width,
        height: node.height,
        parentNode: node.parentNode,
        extent: node.extent,
        zIndex: node.zIndex,
      })),
      edges: edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        type: edge.type || 'smoothstep',
        animated: edge.animated || false,
        data: edge.data,
        label: edge.label,
        markerEnd: edge.markerEnd,
        style: edge.style,
      })),
      customTools: customTools,
      metadata: {
        ...workflowMetadata,
        type: workflowType,
      },
      swarm_config: workflowType === 'swarm' ? swarmConfig : {},
      graph_config: workflowType === 'graph' ? (workflowMetadata.graph_config || {
        max_node_executions: 50,
        execution_timeout: 900,
        node_timeout: null,
        reset_on_revisit: false
      }) : {}
    };
    setHasUnsavedChanges(false);
    const blob = new Blob([JSON.stringify(workflow, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `strands-workflow-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [nodes, edges, customTools, workflowType, swarmConfig, workflowMetadata]);

  const loadWorkflow = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workflow = JSON.parse(e.target.result);
          const loadedNodes = workflow.nodes || [];
          const processedNodes = loadedNodes.map((node) => {
            if (node.type === 'group') {
              const width = node.style?.width || node.width || 300;
              const height = node.style?.height || node.height || 200;
              return {
                ...node,
                style: { ...node.style, width, height },
                zIndex: node.zIndex !== undefined ? node.zIndex : 0,
              };
            } else {
              const parentGroup = loadedNodes.find(
                n => n.type === 'group' && n.data.childNodes?.includes(node.id)
              );
              if (parentGroup) {
                return {
                  ...node,
                  parentNode: parentGroup.id,
                  extent: node.extent || 'parent',
                  zIndex: node.zIndex !== undefined ? node.zIndex : 1000,
                };
              }
              return {
                ...node,
                zIndex: node.zIndex !== undefined ? node.zIndex : 100,
              };
            }
          });
          const sortedNodes = [...processedNodes].sort((a, b) => {
            if (a.type === 'group' && b.type !== 'group') return -1;
            if (a.type !== 'group' && b.type === 'group') return 1;
            if (a.parentNode && !b.parentNode) return 1;
            if (!a.parentNode && b.parentNode) return -1;
            if (b.parentNode === a.id) return -1;
            if (a.parentNode === b.id) return 1;
            return 0;
          });
          const loadedEdges = (workflow.edges || []).map((edge) => ({
            ...edge,
            type: edge.type || 'smoothstep',
            animated: edge.animated || false,
            pathOptions: { borderRadius: 12 },
            markerEnd: edge.markerEnd || {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
              color: '#64748b',
            },
            style: edge.style || {
              strokeWidth: 2,
              stroke: '#64748b',
            },
          }));
          setNodes(sortedNodes);
          setEdges(loadedEdges);
          setCustomTools(workflow.customTools || []);
          setWorkflowType(workflow.metadata?.type || 'single');
        } catch (err) {
          console.error('Failed to load workflow:', err);
        }
      };
      reader.readAsText(file);
    }
    event.target.value = '';
  }, [setNodes, setEdges]);

  const clearWorkflow = useCallback(() => {
    if (window.confirm('Clear the entire workflow?')) {
      setNodes([]);
      setEdges([]);
    }
  }, [setNodes, setEdges]);

  return (
    <div 
      className={`agent-designer-container ${standalone ? 'standalone' : 'embedded'}`}
      style={{ display: visible ? (standalone ? 'flex' : 'contents') : 'none' }}
    >
      <div
        className={`panel panel-left ${leftPanelOpen ? '' : 'collapsed'}`}
        style={{
          width: leftPanelOpen ? 'var(--panel-width)' : '0',
          minWidth: leftPanelOpen ? 'var(--panel-width)' : '0',
        }}
      >
        <ToolLibrary
          builtinTools={builtinTools}
          customTools={customTools}
          onAddCustomTool={handleAddCustomTool}
          onEditCustomTool={handleEditCustomTool}
          onDeleteCustomTool={handleDeleteCustomTool}
          selectedAgent={selectedNode?.type === 'agent' ? selectedNode : null}
          onToggleToolForAgent={handleToggleToolForAgent}
          onClose={() => setLeftPanelOpen(false)}
        />
      </div>

      <div className="canvas-container">
        <WorkflowProvider value={workflowContextValue}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onSelectionChange={onSelectionChange}
            onEdgeClick={onEdgeClick}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            selectNodesOnDrag={false}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#2a303b" gap={20} size={1} />
            <Controls position="bottom-left" />
            <MiniMap
              position="bottom-right"
              nodeColor={(node) => {
                if (node.type === 'group') return node.data.color || '#6b7280';
                return '#6366f1';
              }}
            />
          </ReactFlow>
        </WorkflowProvider>

        <div className="canvas-hints">
          <span className="kbd">Del</span>
          <span>delete</span>
          <span className="kbd">Shift</span>
          <span>+click multi-select</span>
        </div>

        <Toolbar
          onAddNode={addNode}
          onSave={saveWorkflow}
          onLoad={loadWorkflow}
          onClear={clearWorkflow}
          workflowType={workflowType}
          onWorkflowTypeChange={setWorkflowType}
          onCreateGroup={groupSelectedNodes}
          selectedNodesCount={selectedElements.nodes.length}
          onToggleLeftPanel={() => setLeftPanelOpen(prev => !prev)}
          onToggleRightPanel={() => setRightPanelOpen(prev => !prev)}
          onToggleBottomPanel={() => setBottomPanelOpen(prev => !prev)}
          leftPanelOpen={leftPanelOpen}
          rightPanelOpen={rightPanelOpen}
          bottomPanelOpen={bottomPanelOpen}
          onKnowledgeBaseChange={refreshTools}
        />
      </div>

      <div
        className={`panel panel-right ${rightPanelOpen ? '' : 'collapsed'}`}
        style={{
          width: rightPanelOpen ? 'var(--panel-width)' : '0',
          minWidth: rightPanelOpen ? 'var(--panel-width)' : '0',
        }}
      >
        <PropertyPanel
          node={selectedNode}
          onUpdate={(data) => selectedNode && updateNodeData(selectedNode.id, data)}
          onDelete={() => selectedNode && deleteNode(selectedNode.id)}
          onSetEntryPoint={() => selectedNode && setEntryPoint(selectedNode.id)}
          providers={providers}
          workflowType={workflowType}
          workflowMetadata={workflowMetadata}
          swarmConfig={swarmConfig}
          onWorkflowUpdate={handleWorkflowUpdate}
          onClose={() => setRightPanelOpen(false)}
          builtinTools={builtinTools}
        />
      </div>

      {showToolEditor && (
        <ToolEditorDialog
          tool={editingTool}
          onSave={handleSaveCustomTool}
          onClose={() => {
            setShowToolEditor(false);
            setEditingTool(null);
          }}
        />
      )}

      {showEdgeConfig && (
        <EdgeConfigDialog
          edge={editingEdge}
          onSave={handleSaveEdgeConfig}
          onClose={() => {
            setShowEdgeConfig(false);
            setEditingEdge(null);
          }}
        />
      )}
    </div>
  );
}

export default AgentDesigner;

